import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Breadcrumb from "@/components/Breadcrumb";
import { IDropdown } from "@/constants/default";
import KpiCard from "./_KpiCard";
import RevenueKpiCard from "./_RevenueKpiCard";
import ExpensesPieChart from "./_ExpensesPieChart";
import {
    BreakevenDrilldown,
    CashflowNestedDrilldown,
    FormulaAside,
    GrossMarginDrilldown,
    // HistoricalSummaryDrilldown, // DISABLED: Not currently used
    MaintenanceDrilldown,
    NetMarginDrilldown,
    OpexRatioDrilldown,
    OccupancyNestedDrilldown,
    RevenueNestedDrilldown,
    RevenueGrowthDrilldown,
    UtilityExpenditureDrilldown,
    VacantDaysDrilldown,
} from "../kpi/_KpiDrilldownPanels";
import {
    useGetExpenseBreakdownQuery,
    useGetFinancialKpisQuery,
    useGetGrowthKpisQuery,
    useGetOperationalKpisQuery,
    useGetPropertiesQuery,
    useGetUtilitiesKpisQuery,
} from "@/services/kpi.service";

const EXCLUDED_UNIT_LABELS = new Set(["SCKYROOM", "BANK"]);
const TOP_CARD_RANGE = "current_year_through_last_completed_month";
const OPERATIONAL_TOP_CARD_RANGE = "ytd";
const EMPTY_CUSTOM_RANGE = { start: "", end: "" };

const getRevenueGrowthLatestYearNote = () => {
    const now = new Date();
    const currentYear = now.getFullYear();

    if (now.getMonth() < 3) {
        const latestCompletedYear = currentYear - 1;
        return `${latestCompletedYear} shows full-year revenue because no ${currentYear} quarter is complete yet.`;
    }

    return `${currentYear} shows revenue from Jan 1 through the end of the last completed quarter.`;
};

export default function KpiDashboardV2() {
    const { t } = useTranslation();
    const [expandedCardId, setExpandedCardId] = useState<string | null>(null);

    // Section refs for scroll behavior on collapse
    const financialSectionRef = useRef<HTMLDivElement>(null);
    const operationalSectionRef = useRef<HTMLDivElement>(null);
    const growthSectionRef = useRef<HTMLDivElement>(null);
    const previousSectionRef = useRef<"financial" | "operational" | "growth" | null>(null);

    const { data: properties = [] } = useGetPropertiesQuery();

    const businessUnits: IDropdown[] = useMemo(
        () =>
            properties
                .filter((property) => !EXCLUDED_UNIT_LABELS.has(String(property.name).trim().toUpperCase()))
                .map((property) => ({ value: property.id, label: property.name })),
        [properties]
    );

    const inactiveUnitIds = useMemo(
        () => new Set(properties.filter((p) => p.status === false).map((p) => p.id)),
        [properties]
    );

    const { data: financialData, isLoading: financialLoading } = useGetFinancialKpisQuery({
        range: TOP_CARD_RANGE,
        level: "L1",
    });
    const { data: operationalData, isLoading: operationalLoading } = useGetOperationalKpisQuery({
        range: OPERATIONAL_TOP_CARD_RANGE,
        level: "L1",
    });
    const { data: maintenanceTopCardData, isLoading: maintenanceTopCardLoading } = useGetOperationalKpisQuery({
        range: TOP_CARD_RANGE,
        level: "L1",
    });
    const { data: growthData, isLoading: growthLoading } = useGetGrowthKpisQuery({
        range: TOP_CARD_RANGE,
        level: "L1",
    });
    const { data: utilitiesData, isLoading: utilitiesLoading } = useGetUtilitiesKpisQuery({
        range: TOP_CARD_RANGE,
    });
    const { data: propertyExpensesData, isLoading: propertyExpensesLoading } = useGetExpenseBreakdownQuery({
        range: TOP_CARD_RANGE,
        scope: "property_related",
        types: ["OPEX", "CAPEX"],
    });
    const { data: nonPropertyExpensesData, isLoading: nonPropertyExpensesLoading } = useGetExpenseBreakdownQuery({
        range: TOP_CARD_RANGE,
        scope: "non_property",
        types: ["OPEX", "CAPEX"],
    });
    const toggleCardExpansion = (cardId: string) => {
        setExpandedCardId((current) => (current === cardId ? null : cardId));
    };

    const focusedSection = (() => {
        switch (expandedCardId) {
            case "financial:gross_margin":
            case "financial:net_margin":
            case "financial:opex_ratio":
            case "financial:cashflow":
            case "financial:breakeven":
            case "financial:revpau":
                return "financial";
            case "operational:occupancy":
            case "operational:vacant_days":
            case "operational:maintenance":
            case "operational:property_expenses":
            case "operational:non_property_expenses":
            case "operational:utilities":
                return "operational";
            case "growth:revenue_growth":
                return "growth";
            default:
                return null;
        }
    })();
    const expandedCardKey = expandedCardId?.split(":")[1] || null;
    const isFocusedView = Boolean(expandedCardId);
    const shouldShowSection = (section: "financial" | "operational" | "growth") =>
        !isFocusedView || focusedSection === section;
    const isFocusedCard = (cardKey: string) => !isFocusedView || expandedCardKey === cardKey;
    const gridClassName = isFocusedView ? "grid grid-cols-1 gap-4" : "grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4";

    // Track which section was focused before collapse
    useEffect(() => {
        if (focusedSection) {
            previousSectionRef.current = focusedSection;
        }
    }, [focusedSection]);

    // Scroll to section heading when card is collapsed
    useEffect(() => {
        if (expandedCardId === null && previousSectionRef.current) {
            const sectionMap = {
                financial: financialSectionRef,
                operational: operationalSectionRef,
                growth: growthSectionRef,
            };

            const targetRef = sectionMap[previousSectionRef.current];

            if (targetRef?.current) {
                targetRef.current.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                    inline: "nearest"
                });
            }

            previousSectionRef.current = null;
        }
    }, [expandedCardId]);

    return (
        <div className="w-full">
            <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:justify-between lg:items-end">
                <Breadcrumb pageName={t("menu.kpi")} isVisable={false} />
            </div>

            {shouldShowSection("financial") && (
                <section className="mb-8 scroll-mt-18" ref={financialSectionRef}>
                    <h3 className="mb-4 flex items-center gap-2 text-xl font-semibold text-black dark:text-white">
                        <i className="fa-light fa-chart-pie text-primary" />
                        {t("kpi.financial_kpis")}
                    </h3>
                    <div className={gridClassName}>
                        {isFocusedCard("gross_margin") && (
                            <KpiCard
                                isExpandable={true}
                                isExpanded={expandedCardId === "financial:gross_margin"}
                                onToggleExpand={() => toggleCardExpansion("financial:gross_margin")}
                                expandedHeaderAside={
                                    <FormulaAside
                                        formula="((Revenue - COGS) / Revenue) * 100"
                                        notes={[
                                            "COGS = LRENT + HANDY + UTIL + COMM + CLEAN + ISP.",
                                            "In the expanded unit-level view, shared CLEAN recorded under SCKYROOM is excluded from each unit's rows.",
                                        ]}
                                    />
                                }
                                drillDownContent={
                                    <GrossMarginDrilldown
                                        title={t("kpi.gross_margin")}
                                        businessUnits={businessUnits}
                                        inactiveUnitIds={inactiveUnitIds}
                                    />
                                }
                                title={t("kpi.gross_margin")}
                                description={t("kpi.gross_margin_desc", { year: new Date().getFullYear() })}
                                value={financialData?.gross_margin}
                                format="percent"
                                icon="fa-percentage"
                                isLoading={financialLoading}
                                isActive={expandedCardId === "financial:gross_margin"}
                            />
                        )}
                        {isFocusedCard("net_margin") && (
                            <KpiCard
                                isExpandable={true}
                                isExpanded={expandedCardId === "financial:net_margin"}
                                onToggleExpand={() => toggleCardExpansion("financial:net_margin")}
                                expandedHeaderAside={
                                    <FormulaAside
                                        formula="((Revenue - COGS - Other Company Expenses) / Revenue) * 100"
                                        notes={[
                                            "COGS = LRENT + HANDY + UTIL + COMM + CLEAN + ISP.",
                                            "Other Company Expenses = INS + SAL + CPF + SUB + SUPP + BC.",
                                            "In the expanded unit-level view, company level COGS and Other Company Expenses are split equally across units active at any time in the selected date range.",
                                        ]}
                                    />
                                }
                                drillDownContent={
                                    <NetMarginDrilldown
                                        title={t("kpi.net_margin")}
                                        businessUnits={businessUnits}
                                        inactiveUnitIds={inactiveUnitIds}
                                    />
                                }
                                title={t("kpi.net_margin")}
                                description={t("kpi.net_margin_desc", { year: new Date().getFullYear() })}
                                value={financialData?.net_margin}
                                format="percent"
                                icon="fa-percentage"
                                isLoading={financialLoading}
                                isActive={expandedCardId === "financial:net_margin"}
                            />
                        )}
                        {isFocusedCard("opex_ratio") && (
                            <KpiCard
                                isExpandable={true}
                                isExpanded={expandedCardId === "financial:opex_ratio"}
                                onToggleExpand={() => toggleCardExpansion("financial:opex_ratio")}
                                expandedHeaderAside={
                                    <FormulaAside
                                        formula="(Operating Expenses / Revenue) * 100"
                                        notes={[
                                            "Operating Expenses = OPEX transactions in scope for the selected unit and period.",
                                            "Revenue = KPI revenue in scope for the selected unit and period.",
                                        ]}
                                    />
                                }
                                drillDownContent={
                                    <OpexRatioDrilldown
                                        title={t("kpi.opex_ratio")}
                                        businessUnits={businessUnits}
                                        inactiveUnitIds={inactiveUnitIds}
                                    />
                                }
                                title={t("kpi.opex_ratio")}
                                description={t("kpi.opex_ratio_desc", { year: new Date().getFullYear() })}
                                value={financialData?.opex_ratio}
                                format="percent"
                                icon="fa-chart-pie"
                                isLoading={financialLoading}
                                isActive={expandedCardId === "financial:opex_ratio"}
                            />
                        )}
                        {isFocusedCard("cashflow") && (
                            <KpiCard
                                isExpandable={true}
                                isExpanded={expandedCardId === "financial:cashflow"}
                                onToggleExpand={() => toggleCardExpansion("financial:cashflow")}
                                expandedHeaderAside={
                                    <FormulaAside
                                        formula="Revenue - COGS - Other Company Expenses"
                                        notes={[
                                            "COGS = LRENT + HANDY + UTIL + COMM + CLEAN + ISP.",
                                            "Other Company Expenses = INS + SAL + CPF + SUB + SUPP + BC.",
                                            "In the expanded unit-level view, CLEAN and Other Company Expenses are excluded from each unit's row.",
                                        ]}
                                    />
                                }
                                drillDownContent={
                                    <CashflowNestedDrilldown
                                        title={t("kpi.cashflow")}
                                        businessUnits={businessUnits}
                                        inactiveUnitIds={inactiveUnitIds}
                                    />
                                }
                                title={t("kpi.cashflow")}
                                description={t("kpi.cashflow_desc", { year: new Date().getFullYear() })}
                                value={financialData?.cashflow}
                                format="currency"
                                icon="fa-dollar-sign"
                                isLoading={financialLoading}
                                isActive={expandedCardId === "financial:cashflow"}
                            />
                        )}
                        {isFocusedCard("breakeven") && (
                            <KpiCard
                                isExpandable={true}
                                isExpanded={expandedCardId === "financial:breakeven"}
                                onToggleExpand={() => toggleCardExpansion("financial:breakeven")}
                                expandedHeaderAside={
                                    <FormulaAside
                                        formula="Initial CAPEX + pre-TA COGS recovered by cumulative monthly cashflow"
                                        notes={[
                                            "This KPI is unit-level only and has no company-level summary value.",
                                            "Monthly cashflow = monthly revenue - monthly COGS.",
                                            "The first month counted starts from the unit's first TA Start date and uses the first-ever lease only.",
                                            "Pre-TA-start CAPEX and COGS are included in the opening recovery balance.",
                                        ]}
                                    />
                                }
                                drillDownContent={
                                    <BreakevenDrilldown
                                        title={t("kpi.breakeven")}
                                        businessUnits={businessUnits}
                                        inactiveUnitIds={inactiveUnitIds}
                                    />
                                }
                                title={t("kpi.breakeven")}
                                description={t("kpi.breakeven_desc")}
                                displayValueText={t("kpi.unit_level_only")}
                                format="number"
                                icon="fa-hourglass-half"
                                isLoading={financialLoading}
                                isActive={expandedCardId === "financial:breakeven"}
                            />
                        )}
                        {isFocusedCard("revpau") && (
                            <RevenueKpiCard
                                isExpandable={true}
                                isExpanded={expandedCardId === "financial:revpau"}
                                onToggleExpand={() => toggleCardExpansion("financial:revpau")}
                                expandedHeaderAside={
                                    <FormulaAside
                                        formula="Revenue = sum of posted N/A deposit transactions"
                                    />
                                }
                                expandedContent={
                                    <RevenueNestedDrilldown
                                        title={t("kpi.revenue")}
                                        businessUnits={businessUnits}
                                        inactiveUnitIds={inactiveUnitIds}
                                    />
                                }
                                title={t("kpi.revenue")}
                                description={t("kpi.revenue_desc", { year: new Date().getFullYear() })}
                                value={financialData?.total_revenue}
                                format="currency"
                                icon="fa-house-building"
                                isLoading={financialLoading}
                                isActive={expandedCardId === "financial:revpau"}
                            />
                        )}
                    </div>
                </section>
            )}

            {shouldShowSection("operational") && (
                <section className="mb-8 scroll-mt-18" ref={operationalSectionRef}>
                    <div className="mb-4">
                        <h3 className="flex items-center gap-2 text-xl font-semibold text-black dark:text-white">
                            <i className="fa-light fa-building text-primary" />
                            {t("kpi.operational_kpis")}
                        </h3>
                    </div>

                    <div className={gridClassName}>
                        {isFocusedCard("occupancy") && (
                            <KpiCard
                                isExpandable={true}
                                isExpanded={expandedCardId === "operational:occupancy"}
                                onToggleExpand={() => toggleCardExpansion("operational:occupancy")}
                                expandedHeaderAside={
                                    <FormulaAside
                                        formula="Average of completed month-end occupancy snapshots"
                                        notes={[
                                            "Each snapshot = occupied rooms / total rooms at month-end * 100.",
                                            "Occupancy uses month-end snapshots, not a /365 formula.",
                                            "Expanded detail shows units first, then rooms within the selected unit.",
                                            "Expanded tables show the current year to date and the previous 4 full calendar years.",
                                        ]}
                                    />
                                }
                                drillDownContent={
                                    <OccupancyNestedDrilldown
                                        title={t("kpi.occupancy_rate")}
                                        businessUnits={businessUnits}
                                        inactiveUnitIds={inactiveUnitIds}
                                    />
                                }
                                title={t("kpi.occupancy_rate")}
                                description={t("kpi.occupancy_rate_desc")}
                                value={operationalData?.occupancy_rate}
                                format="percent"
                                icon="fa-house-building"
                                isLoading={operationalLoading}
                                isActive={expandedCardId === "operational:occupancy"}
                            />
                        )}
                        {isFocusedCard("vacant_days") && (
                            <KpiCard
                                isExpandable={true}
                                isExpanded={expandedCardId === "operational:vacant_days"}
                                onToggleExpand={() => toggleCardExpansion("operational:vacant_days")}
                                expandedHeaderAside={
                                    <FormulaAside
                                        formula="Total Vacant Days of All Rooms / Number of Available Rooms in Period"
                                        notes={[
                                            "Vacant days are based on date gaps between leases.",
                                            "For company and unit values, the number of available rooms are weighted according to the number of available days in the period.",
                                            "The Vacant Days top card continues to use Jan 1 to today, with the denominator based on elapsed days in the year.",
                                            "Expanded detail shows units first, then rooms within the selected unit.",
                                        ]}
                                    />
                                }
                                drillDownContent={
                                    <VacantDaysDrilldown
                                        title={t("kpi.vacant_days")}
                                        businessUnits={businessUnits}
                                        inactiveUnitIds={inactiveUnitIds}
                                    />
                                }
                                title={t("kpi.vacant_days")}
                                description={t("kpi.vacant_days_desc", { year: new Date().getFullYear() })}
                                value={operationalData?.vacant_days}
                                displayValueText={
                                    operationalData?.vacant_days != null
                                        ? <>{operationalData.vacant_days.toFixed(1)} days per room <span className="text-sm font-normal">(out of {Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 1).getTime()) / (1000 * 60 * 60 * 24))} days)</span></>
                                        : undefined
                                }
                                format="days"
                                icon="fa-bed-empty"
                                isLoading={operationalLoading}
                                isActive={expandedCardId === "operational:vacant_days"}
                            />
                        )}
                        {isFocusedCard("maintenance") && (
                            <KpiCard
                                isExpandable={true}
                                isExpanded={expandedCardId === "operational:maintenance"}
                                onToggleExpand={() => toggleCardExpansion("operational:maintenance")}
                                expandedHeaderAside={
                                    <FormulaAside
                                        formula="Total Maintenance Cost = HANDY withdrawals - HANDY deposits"
                                        notes={[
                                            "This metric uses only the HANDY transaction code.",
                                            "HANDY deposits are treated as refunds and reduce maintenance spend.",
                                            "The top card uses Jan 1 through the end of the last completed month.",
                                            "Expanded detail compares units across current-year YTD and the previous 4 full calendar years.",
                                            "Expanding a unit shows the monthly HANDY chart with a HANDY transaction table underneath.",
                                        ]}
                                    />
                                }
                                drillDownContent={
                                    <MaintenanceDrilldown
                                        monthlyMaintenance={maintenanceTopCardData?.monthly_maintenance || []}
                                        maintenanceUnits={maintenanceTopCardData?.maintenance_units || []}
                                        inactiveUnitIds={inactiveUnitIds}
                                    />
                                }
                                title={t("kpi.maintenance_cost")}
                                description={t("kpi.maintenance_cost_desc", { year: new Date().getFullYear() })}
                                value={maintenanceTopCardData?.total_maintenance_cost}
                                format="currency"
                                icon="fa-screwdriver-wrench"
                                isLoading={maintenanceTopCardLoading}
                                isActive={expandedCardId === "operational:maintenance"}
                            />
                        )}
                        {isFocusedCard("property_expenses") && (
                            <KpiCard
                                isExpandable={true}
                                isExpanded={expandedCardId === "operational:property_expenses"}
                                onToggleExpand={() => toggleCardExpansion("operational:property_expenses")}
                                drillDownContent={
                                    <ExpensesPieChart
                                        dateRange={TOP_CARD_RANGE}
                                        customRange={EMPTY_CUSTOM_RANGE}
                                        scope="property_related"
                                        storageKey="kpi-v2-property-related-expenses"
                                        title={t("kpi.property_related_expenses")}
                                        helperNote="The top card uses Jan 1 through the end of the last completed month. This metric may still benefit from its own date selector in the expanded view."
                                    />
                                }
                                title={t("kpi.property_related_expenses")}
                                description={t("kpi.property_related_expenses_desc", { year: new Date().getFullYear() })}
                                value={propertyExpensesData?.total_amount}
                                format="currency"
                                icon="fa-chart-pie"
                                isLoading={propertyExpensesLoading}
                                isActive={expandedCardId === "operational:property_expenses"}
                            />
                        )}
                        {isFocusedCard("non_property_expenses") && (
                            <KpiCard
                                isExpandable={true}
                                isExpanded={expandedCardId === "operational:non_property_expenses"}
                                onToggleExpand={() => toggleCardExpansion("operational:non_property_expenses")}
                                drillDownContent={
                                    <ExpensesPieChart
                                        dateRange={TOP_CARD_RANGE}
                                        customRange={EMPTY_CUSTOM_RANGE}
                                        scope="non_property"
                                        storageKey="kpi-v2-non-property-expenses"
                                        title={t("kpi.non_property_related_expenses")}
                                        helperNote="The top card uses Jan 1 through the end of the last completed month. This metric may still benefit from its own date selector in the expanded view."
                                    />
                                }
                                title={t("kpi.non_property_related_expenses")}
                                description={t("kpi.non_property_related_expenses_desc", { year: new Date().getFullYear() })}
                                value={nonPropertyExpensesData?.total_amount}
                                format="currency"
                                icon="fa-building-circle-xmark"
                                isLoading={nonPropertyExpensesLoading}
                                isActive={expandedCardId === "operational:non_property_expenses"}
                            />
                        )}
                        {isFocusedCard("utilities") && (
                            <KpiCard
                                isExpandable={true}
                                isExpanded={expandedCardId === "operational:utilities"}
                                onToggleExpand={() => toggleCardExpansion("operational:utilities")}
                                expandedHeaderAside={
                                    <FormulaAside
                                        formula="Utility Expenditure = UTIL expenses"
                                        notes={[
                                            "Providers are parsed from the current utility descriptions.",
                                            "The top card uses Jan 1 through the end of the last completed month.",
                                            "The expanded table shows Avg plus the latest 12 monthly columns, each compared against the 24-month average.",
                                            "Provider toggles update the displayed 24-month average.",
                                            "Chart hover also shows delta versus the displayed 24-month average.",
                                        ]}
                                    />
                                }
                                drillDownContent={
                                    <UtilityExpenditureDrilldown
                                        title={t("kpi.utility_expenditure")}
                                        businessUnits={businessUnits}
                                        inactiveUnitIds={inactiveUnitIds}
                                    />
                                }
                                title={t("kpi.utility_expenditure")}
                                description={t("kpi.utility_expenditure_desc", { year: new Date().getFullYear() })}
                                value={utilitiesData?.summary?.total_spend}
                                format="currency"
                                icon="fa-bolt"
                                isLoading={utilitiesLoading}
                                isActive={expandedCardId === "operational:utilities"}
                            />
                        )}
                    </div>
                </section>
            )}

            {shouldShowSection("growth") && (
                <section className="mb-8 scroll-mt-18" ref={growthSectionRef}>
                    <h3 className="mb-4 flex items-center gap-2 text-xl font-semibold text-black dark:text-white">
                        <i className="fa-light fa-chart-line text-primary" />
                        {t("kpi.growth_kpis")}
                    </h3>
                    <div className={gridClassName}>
                        {isFocusedCard("revenue_growth") && (
                            <KpiCard
                                isExpandable={true}
                                isExpanded={expandedCardId === "growth:revenue_growth"}
                                onToggleExpand={() => toggleCardExpansion("growth:revenue_growth")}
                                expandedHeaderAside={
                                    <FormulaAside
                                        formula="Revenue = sum of posted N/A deposit transactions"
                                        notes={[
                                            "Main table shows revenue totals for each selected period.",
                                            getRevenueGrowthLatestYearNote(),
                                            "Click a row to expand and view quarterly revenue breakdown (Q1-Q4).",
                                        ]}
                                    />
                                }
                                drillDownContent={
                                    <RevenueGrowthDrilldown
                                        title={t("kpi.revenue_growth")}
                                        businessUnits={businessUnits}
                                        inactiveUnitIds={inactiveUnitIds}
                                    />
                                }
                                title={t("kpi.revenue_growth")}
                                description={
                                    <>
                                        {t("kpi.revenue_growth_desc", { year: new Date().getFullYear() })}
                                        <br />
                                        <strong>Further enhancements to be made in future development.</strong>
                                    </>
                                }
                                displayValueText={t("kpi.unit_level_only")}
                                format="percent"
                                icon="fa-chart-line"
                                isLoading={growthLoading}
                                isActive={expandedCardId === "growth:revenue_growth"}
                            />
                        )}
                    </div>
                </section>
            )}
        </div>
    );
}
